import { useState, useEffect, useMemo } from 'react';
import { buildPeriodQueryRange, useDebounce } from '@practium/shared';
import type { FilterGroup } from '@practium/ui';
import { mapListPageLineChart, mapListPagePieData } from '@practium/features/dashboard';
import {
	useGetAnalysesStats,
	useGetPlantAnalyses,
	useGetSoilAnalyses,
	useExportSoilAnalysis,
} from '@practium/features/api';
import type {
	SoilAnalysis,
	PlantAnalysis,
	AnalysesTab,
	AnalysesPermissions,
} from '../types/analyses.types';

const PAGE_SIZE = 10;

const resolveRefLabel = (value: string | { name?: string; _id?: string } | null | undefined) => {
	if (!value) return undefined;
	if (typeof value === 'string') return value;
	return value.name ?? value._id;
};

const FILTER_GROUPS: FilterGroup[] = [
	{
		label: 'Ordernar por:',
		selectionMode: 'single',
		presentation: 'radio',
		options: [
			{ value: 'ordem:recentes', label: 'Mais recentes', countsAsActive: false },
			{ value: 'ordem:antigos', label: 'Mais antigos' },
		],
	},
	{
		label: 'Período',
		options: [{ value: 'periodo:custom', label: 'Personalizado' }],
	},
];

export interface UseAnalysesPageProps {
	permissions: AnalysesPermissions;
	areaId?: string;
	initialTab?: AnalysesTab;
}

export const useAnalysesPage = ({
	permissions,
	areaId,
	initialTab = 'soil',
}: UseAnalysesPageProps) => {
	const [activeTab, setActiveTab] = useState<AnalysesTab>(initialTab);
	const [searchQuery, setSearchQuery] = useState('');
	const [sortAsc, setSortAsc] = useState<true | undefined>();
	const [filterValues, setFilterValues] = useState<string[]>(['ordem:recentes']);
	const oldest = filterValues.includes('ordem:antigos') ? true : undefined;
	const [customDateFrom, setCustomDateFrom] = useState('');
	const [customDateTo, setCustomDateTo] = useState('');
	const [page, setPage] = useState(1);
	const searchContext = useMemo(
		() => ({ tab: activeTab, query: searchQuery }),
		[activeTab, searchQuery]
	);
	const debouncedSearch = useDebounce(searchContext, 400);
	const debouncedSearchQuery =
		searchQuery.trim() && debouncedSearch.tab === activeTab ? debouncedSearch.query : '';

	const periodoFilters = useMemo(
		() => filterValues.filter((v) => v.startsWith('periodo:')).map((v) => v.slice(8)),
		[filterValues]
	);

	const { startDate, endDate } = useMemo(
		() => buildPeriodQueryRange(periodoFilters, customDateFrom, customDateTo),
		[periodoFilters, customDateFrom, customDateTo]
	);
	const apiFilter = debouncedSearchQuery.trim() || undefined;

	const soilQuery = useGetSoilAnalyses({
		areaId,
		startDate,
		endDate,
		filter: apiFilter,
		sortAsc,
		oldest,
		page,
		limit: PAGE_SIZE,
		enabled: activeTab === 'soil',
	});

	const plantQuery = useGetPlantAnalyses({
		areaId,
		startDate,
		endDate,
		filter: apiFilter,
		sortAsc,
		oldest,
		page,
		limit: PAGE_SIZE,
		enabled: activeTab === 'plant',
	});

	const chartsQuery = useGetAnalysesStats({
		areaId,
		startDate,
		endDate,
	});
	const exportSoilAnalysisMutation = useExportSoilAnalysis();
	const soilAnalysesData = soilQuery.data;
	const plantAnalysesData = plantQuery.data;
	const activeQuery = activeTab === 'soil' ? soilQuery : plantQuery;
	const chartsData = chartsQuery.isPlaceholderData ? undefined : chartsQuery.data;

	const soilAnalyses = useMemo<SoilAnalysis[]>(
		() =>
			soilAnalysesData.map((analysis) => ({
				_id: analysis._id,
				name: analysis.name,
				state:
					analysis.state ??
					(typeof analysis.prop === 'object' ? analysis.prop?.state : undefined) ??
					'',
				sampledAt: analysis.sampledAt,
				status: 'Realizada',
				property: resolveRefLabel(analysis.prop),
				createdAt: analysis.createdAt ?? '',
			})),
		[soilAnalysesData]
	);

	const plantAnalyses = useMemo<PlantAnalysis[]>(
		() =>
			plantAnalysesData.map((analysis) => ({
				_id: analysis._id,
				name: analysis.name,
				culture: analysis.culture,
				sampleAt: analysis.sampleAt,
				region: analysis.region,
				status: 'Realizada',
				property: resolveRefLabel(analysis.prop),
				createdAt: analysis.createdAt ?? '',
			})),
		[plantAnalysesData]
	);

	useEffect(() => {
		setPage(1);
	}, [areaId, debouncedSearchQuery, filterValues, customDateFrom, customDateTo, sortAsc]);

	const handleTabChange = (tab: AnalysesTab) => {
		setActiveTab(tab);
		setSearchQuery('');
		setSortAsc(undefined);
		setFilterValues(['ordem:recentes']);
		setCustomDateFrom('');
		setCustomDateTo('');
		setPage(1);
	};

	const totalPages =
		activeQuery.isPlaceholderData || activeQuery.isError
			? 1
			: Math.max(1, activeQuery.pagination?.totalPages ?? 1);

	const exportSoilAnalysis = async (format: 'pdf' | 'xlsx') => {
		const { file, filename } = await exportSoilAnalysisMutation.mutateAsync({
			format,
			areaId,
			startDate,
			endDate,
			filter: apiFilter,
			sortAsc,
			oldest,
		});
		const url = URL.createObjectURL(file);
		const link = document.createElement('a');
		try {
			link.href = url;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
		} finally {
			link.remove();
			window.setTimeout(() => URL.revokeObjectURL(url), 1000);
		}
	};

	const analysesPieData = useMemo(() => mapListPagePieData(chartsData?.pie?.items), [chartsData]);

	const {
		lineData: analysesLineData,
		mainValue: chartMainValue,
		trendValue: chartTrendValue,
		trendDirection: chartTrendDirection,
		footerText: analysesFooterText,
	} = useMemo(() => mapListPageLineChart(chartsData?.line), [chartsData]);

	return {
		activeTab,
		setActiveTab: handleTabChange,
		soilAnalyses,
		plantAnalyses,
		filteredSoil: soilAnalyses,
		filteredPlant: plantAnalyses,
		searchQuery,
		setSearchQuery,
		sortAsc: Boolean(sortAsc),
		toggleSort: () => setSortAsc((v) => (v ? undefined : true)),
		filterValues,
		setFilterValues: (values: string[]) =>
			setFilterValues(
				values.some((value) => value.startsWith('ordem:')) ? values : [...values, 'ordem:recentes']
			),
		filterGroups: FILTER_GROUPS,
		customDateFrom,
		setCustomDateFrom,
		customDateTo,
		setCustomDateTo,
		page,
		setPage,
		totalPages,
		isTableLoading: activeQuery.isPending || activeQuery.isPlaceholderData,
		isTableFetching: activeQuery.isFetching,
		isTableError: activeQuery.isError,
		refetchTable: activeQuery.refetch,
		isChartsLoading: chartsQuery.isPending || chartsQuery.isPlaceholderData,
		isChartsFetching: chartsQuery.isFetching,
		isChartsError: chartsQuery.isError,
		permissions,
		exportSoilAnalysis,
		isExportingSoilAnalysis: exportSoilAnalysisMutation.isPending,
		analysesPieData,
		analysesLineData,
		chartMainValue,
		chartTrendValue,
		chartTrendDirection,
		analysesFooterText,
	};
};
